<script>
  import moment from "moment";
  import { onMount } from "svelte";
  import { navigate, Link } from "svelte-routing";
  import Button, { Label, Icon } from "@smui/button";
  import { Title } from "@smui/paper";
  import { apiGet, apiCreate, download, openPage } from "../httpCommon";
  import { ResourceShow } from "../components";
  import { InvoiceShow } from "../invoices";
  import SalesChart from "./SalesChart.svelte";
  import CateringRequirementsCard from "./CateringRequirementsCard.svelte";

  export let id;
  export let title;
  export let data = null;
  let basePath = `/popups/${id}`;
  let backStatus, nextStatus;

  let salesData = null;

  $: {
    if (data) {
      title = `${moment(data.start_time).format("Do MMM")} @ ${
        data.venue_name
      }`;
    }
  }

  $: canCancel = data && ["proposed", "scheduled"].includes(data.status);

  $: {
    backStatus = null;
    nextStatus = null;
    if (data) {
      if (data.status === "proposed") {
        if (data.id && data.vendor_id) {
          nextStatus = "scheduled";
        }
      } else if (data.status === "scheduled") {
        if (!data.invoice) {
          backStatus = "proposed";
        }
        nextStatus = "completed";
      } else if (data.status === "completed" && !data.invoice) {
        backStatus = "scheduled";
      } else if (data.status === "cancelled") {
        backStatus = "proposed";
      }
    }
  }

  async function createInvoice() {
    try {
      data.invoice = await apiCreate("/invoices/popup", { popup_id: id });
    } catch (error) {
      console.log("Error: ", error);
    }
  }

  async function downloadCateringInvoice() {
    await download(`/invoice/popup/${id}.pdf`, `Invoice_${data.id}.pdf`);
  }

  async function downloadInfo() {
    await download(
      `/popups/${id}/info.pdf`,
      `Food Disco Info ${data.venue_name}.pdf`
    );
  }

  function openMenu() {
    openPage(`/popups/${id}/menu`);
  }

  $: {
    if (data && data.sales_summary && data.sales_summary.count > 0) {
      getSalesData();
    }
  }

  async function getSalesData() {
    try {
      const filter = JSON.stringify({ popup_id: id });
      salesData = await apiGet("/sales/grouped", null, { filter });
    } catch (err) {}
  }
</script>

<style>
  .summary {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  }
  .summary table {
    white-space: nowrap;
    margin: 0 20px;
  }
  .summary table th {
    text-align: right;
  }

  @media (max-width: 1000px) {
    .statuses {
      display: flex;
    }
    .statuses :global(span.mdc-button__label) {
      display: none;
    }
    .actions :global(span.mdc-button__label) {
      display: none;
    }
  }
</style>

<svelte:head>
  <script
    src="https://cdnjs.cloudflare.com/ajax/libs/Chart.js/2.8.0/Chart.min.js"
    integrity="sha256-Uv9BNBucvCPipKQ2NS9wYpJmi8DTOEfTA/nH2aoJALw="
    crossorigin="anonymous">

  </script>
</svelte:head>

<ResourceShow resource="popups" bind:data {id}>
  <div slot="title" let:load let:close let:loading>
    <Title class="flex">
      <span>{title}</span>
      <span class="actions">
        <Button on:click={load} disabled={loading}>
          <Icon class="material-icons">replay</Icon>
          <Label>Reload</Label>
        </Button>
        <Button
          on:click={() => navigate(`${basePath}/edit`)}
          disabled={loading}>
          <Icon class="material-icons">settings_applications</Icon>
          <Label>Edit</Label>
        </Button>
        <Button on:click={close}>
          <Icon class="material-icons">chevron_left</Icon>
          <Label>Back</Label>
        </Button>
      </span>
    </Title>
  </div>
  <div slot="content" let:save let:load>
    {#if data}
      <div class="flex">
        <div>
          {#if data.event_no}
            <p>
              <strong>Event No.:</strong>
              {data.event_no}
            </p>
          {/if}
          <div class="flex">
            <p>
              <span>
                <strong>Arrival Time:</strong>
                {moment(data.arrival_time).format('HH:mm')}
              </span>
              <br />
              <span>
                <strong>Service Time:</strong>
                {moment(data.start_time).format('HH:mm')} - {moment(data.end_time).format('HH:mm')}
              </span>
              <br />
              <span>
                <strong>Status:</strong>
                {data.status}
              </span>
            </p>
            <div class="statuses">
              {#if backStatus}
                <div>
                  <Button
                    on:click={() => {
                      data.status = backStatus;
                      save();
                    }}>
                    <Icon class="material-icons">skip_previous</Icon>
                    <Label>revert to {backStatus}</Label>
                  </Button>
                </div>
              {/if}

              {#if nextStatus}
                <div>
                  <Button
                    on:click={() => {
                      data.status = nextStatus;
                      save();
                    }}>
                    <Label>Move to {nextStatus}</Label>
                    <Icon class="material-icons">skip_next</Icon>
                  </Button>
                </div>
              {/if}
              {#if canCancel}
                <div>
                  <Button
                    on:click={() => {
                      data.status = 'cancelled';
                      save();
                    }}>
                    <Label>Cancel event</Label>
                    <Icon class="material-icons">delete</Icon>
                  </Button>
                </div>
              {/if}
            </div>
          </div>
          <p>
            <span class="capitalize">{data.popup_type}</span>
            event
            {#if data.vendor_id}
              by
              <Link to="/vendors/{data.vendor_id}">{data.vendor_name}</Link>
            {:else}(vendor not yet selected){/if}
            {#if data.description}
              <br />
              <span>{data.description}</span>
            {/if}
          </p>
          {#if data.popup_type === 'catered'}
            <p>
              <span>
                <strong>Event Value:</strong>
                {data.catered_value} ({data.vat_inclusive ? 'incl' : 'excl'}
                VAT)
              </span>
            </p>
            <CateringRequirementsCard data={data.catering_requirements} />
          {/if}
          {#if data.popup_type === 'popup'}
            <p>
              <span>
                <strong>Online Ordering:</strong>
                {data.online_order_allowed}
              </span>
              <br />
              <span>
                <strong>Pickup Time Allowed:</strong>
                {data.pickup_time_allowed}
              </span>
            </p>
            {#if data.sales_summary}
              <div class="summary">
                <table>
                  <tr>
                    <th>Takings:</th>
                    <td class="right">{data.sales_summary.total_takings}</td>
                  </tr>
                  <tr>
                    <th>Discounts:</th>
                    <td class="right">{data.sales_summary.total_discounts}</td>
                  </tr>
                  <tr>
                    <th>VAT:</th>
                    <td class="right">{data.sales_summary.total_tax_amount}</td>
                  </tr>
                  <tr>
                    <th>Refunded:</th>
                    <td class="right">{data.sales_summary.total_refunded}</td>
                  </tr>
                  <tr>
                    <th>CC Fees:</th>
                    <td class="right">{data.sales_summary.total_cc_fees}</td>
                  </tr>
                  <tr>
                    <th>Commission:</th>
                    <td class="right">
                      ({data.commission_rate_pct}%) {data.sales_summary.total_commission}
                    </td>
                  </tr>
                </table>
                {#if data.discounts_summary && data.discounts_summary.length}
                  <table>
                    <tr>
                      <th>Discount Code</th>
                      <th>Amount</th>
                    </tr>
                    {#each data.discounts_summary as discount}
                      <tr>
                        <th>{discount.discount_code}</th>
                        <td class="right">{discount.discount_amount}</td>
                      </tr>
                    {/each}
                  </table>
                {/if}
                {#if data.item_summary}
                  <p />
                  <table>
                    <tr>
                      <th>Item</th>
                      <th>Count</th>
                      <th>Total</th>
                      <th>VAT</th>
                    </tr>
                    {#each data.item_summary as item}
                      <tr>
                        <th class="right">{item.item_name}:</th>
                        <td class="right">{item.count}</td>
                        <td class="right">{item.total_amount}</td>
                        <td class="right">{item.tax_amount}</td>
                      </tr>
                    {/each}
                  </table>
                {/if}
              </div>
            {/if}
          {:else if data.popup_type === 'catered'}
            {#if ['scheduled', 'completed'].includes(data.status)}
              <p>
                <Button on:click={downloadCateringInvoice}>
                  get catering invoice
                  <Icon class="material-icons">save_alt</Icon>
                </Button>
              </p>
            {/if}
          {/if}
        </div>

      </div>
      {#if data.status === 'scheduled'}
        <p />
        <Button on:click={downloadInfo}>
          <Label>Info PDF</Label>
          <Icon class="material-icons">save_alt</Icon>
        </Button>
      {/if}
      {#if data.invoice}
        <InvoiceShow data={data.invoice} topLevel={false} on:deleted={load} />
      {:else if data.status == 'completed' && data.commission_rate_pct > 0}
        <p>
          <Button on:click={createInvoice}>
            <Label>Create Fee Invoice</Label>
          </Button>
        </p>
      {/if}
    {/if}
    {#if salesData}
      <SalesChart labels={salesData.labels} data={salesData.counts} />
      <!--
      <Button on:click={openMenu}>
        <Label>View Menu</Label>
        <Icon class="material-icons">menu</Icon>
      </Button>
        -->
    {/if}
  </div>
</ResourceShow>
